/**
 * Full-width product slider: custom scroll-snap carousel with true infinite
 * looping and reliable one-product-at-a-time navigation.
 *
 * Built independently instead of using the theme's native slideshow
 * component — that component assumes single full-width slides and its
 * infinite-loop / arrow logic doesn't translate cleanly to this "N products
 * visible at once, always move by one" layout, which caused erratic jumps.
 *
 * The infinite loop works by cloning a few slides at each end of the track.
 * Scrolling forward past the real last slide silently continues into
 * identical-looking clones; once the scroll settles there, we instantly (no
 * animation) snap back to the equivalent real position — invisible to the
 * visitor, and always feels like continuous forward motion, never a jump
 * backward.
 */
function setUpSlider(root) {
  const track = root.querySelector('[data-fw-track]');
  const prevButton = root.querySelector('[data-fw-prev]');
  const nextButton = root.querySelector('[data-fw-next]');
  const dotsContainer = root.querySelector('[data-fw-dots]');
  if (!track) return;

  const originalSlides = Array.from(track.children);
  const count = originalSlides.length;
  if (count === 0) return;

  const perPage = parseInt(getComputedStyle(root).getPropertyValue('--fw-slider-per-page'), 10) || 3;
  const cloneCount = Math.min(perPage, count);

  // Clone slides at both ends so scrolling never has to jump backward to loop.
  const endClones = originalSlides.slice(-cloneCount).map((el) => el.cloneNode(true));
  endClones.forEach((clone) => track.insertBefore(clone, track.firstChild));

  const startClones = originalSlides.slice(0, cloneCount).map((el) => el.cloneNode(true));
  startClones.forEach((clone) => track.appendChild(clone));

  const allSlides = Array.from(track.children);
  let currentIndex = cloneCount; // index of the first real slide within allSlides
  let dots = [];

  function scrollToIndex(index, smooth) {
    const target = allSlides[index];
    if (!target) return;
    track.scrollTo({ left: target.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  }

  function updateDots() {
    if (!dots.length) return;
    const realIndex = ((currentIndex - cloneCount) % count + count) % count;
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === realIndex));
  }

  function goTo(index, smooth = true) {
    currentIndex = index;
    scrollToIndex(currentIndex, smooth);
    updateDots();
  }

  function next() {
    goTo(currentIndex + 1);
  }

  function prev() {
    goTo(currentIndex - 1);
  }

  // Snap the very first render into place without any animation.
  scrollToIndex(currentIndex, false);

  // After the visitor's scroll (or our own) settles, check whether we've
  // landed in clone territory and, if so, silently reposition to the
  // matching real slide — this is what makes the loop feel truly continuous.
  let settleTimer;
  track.addEventListener('scroll', () => {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      allSlides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      currentIndex = nearestIndex;

      if (currentIndex >= cloneCount + count) {
        currentIndex -= count;
        scrollToIndex(currentIndex, false);
      } else if (currentIndex < cloneCount) {
        currentIndex += count;
        scrollToIndex(currentIndex, false);
      }

      updateDots();
    }, 120);
  });

  if (prevButton) prevButton.addEventListener('click', () => prev());
  if (nextButton) nextButton.addEventListener('click', () => next());

  if (dotsContainer) {
    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'fw-product-slider__dot';
      dot.setAttribute('aria-label', `Go to product ${i + 1}`);
      if (i === 0) dot.classList.add('is-active');
      dot.addEventListener('click', () => goTo(cloneCount + i));
      dotsContainer.append(dot);
    }
    dots = Array.from(dotsContainer.children);
  }

  const autoplayMs = parseInt(root.dataset.autoplay, 10);
  if (autoplayMs) {
    let timer = setInterval(next, autoplayMs);

    root.addEventListener('mouseenter', () => clearInterval(timer));
    root.addEventListener('mouseleave', () => {
      clearInterval(timer);
      timer = setInterval(next, autoplayMs);
    });
  }
}

document.querySelectorAll('[data-fw-slider]').forEach(setUpSlider);